import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { LayoutGrid, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import {
  usePromoTilesAdmin,
  useCreatePromoTile,
  useUpdatePromoTile,
  useDeletePromoTile,
} from '@/lib/productCatalog'
import { productMediaApi } from '@/api/productMedia'
import { userMessage } from '@/api/errors'
import type { PromoTile, PromoTileInput } from '@/types/ecommerce'
import EditModal, { type EditField } from '@/components/crud/EditModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import { InlineToggle } from '@/components/crud/InlineToggle'
import { resolveMediaUrl } from '@/utils/mediaUrl';

const fields: EditField[] = [
  {
    key: 'imageUrl',
    label: 'Image',
    required: true,
    type: 'file',
    accept: 'image/jpeg,image/png,image/gif,image/webp',
    previewKind: 'image',
    uploadFn: (file) => productMediaApi.uploadPromoTileImage(file),
    hint: 'JPEG, PNG, GIF or WEBP, up to 8MB.',
  },
  { key: 'title', label: 'Title', placeholder: 'Loyalty rewards' },
  { key: 'description', label: 'Blurb', type: 'textarea', placeholder: 'Earn points on every order and redeem them for discounts.' },
  { key: 'linkText', label: 'Link text', placeholder: 'Find out more' },
  { key: 'linkUrl', label: 'Link URL', placeholder: '/account/loyalty' },
  { key: 'sortOrder', label: 'Sort order', type: 'number', required: true },
  { key: 'active', label: 'Active', type: 'checkbox', placeholder: 'Show on the storefront homepage' },
]

const toInput = (values: Record<string, any>): PromoTileInput => ({
  imageUrl: values.imageUrl,
  title: values.title || undefined,
  description: values.description || undefined,
  linkUrl: values.linkUrl || undefined,
  linkText: values.linkText || undefined,
  sortOrder: Number.isFinite(values.sortOrder) ? values.sortOrder : 0,
  active: !!values.active,
})

function PromoTilesPage() {
  const { data: tiles = [], isLoading, isError, error, isFetching, refetch } = usePromoTilesAdmin()
  const create = useCreatePromoTile()
  const update = useUpdatePromoTile()
  const del = useDeletePromoTile()
  const confirmDelete = useDeleteConfirm()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<PromoTile | null>(null)

  const sorted = [...tiles].sort((a, b) => a.sortOrder - b.sortOrder)

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <LayoutGrid className="w-6 h-6" /> Promo Tiles
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Homepage "more ways to shop" grid on the ecommerce storefront. Ordered by sort order; only active tiles appear.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Tile
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading tiles…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load promo tiles: {(error as Error)?.message}.</p>
        ) : sorted.length === 0 ? (
          <p className="p-6 text-gray-500">No promo tiles yet — create one to populate the storefront grid.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Preview</th>
                <th className="px-4 py-2">Title</th>
                <th className="px-4 py-2">Link</th>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => (
                <tr key={t.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3">
                    <img src={resolveMediaUrl(t.imageUrl)} alt="" className="h-12 w-20 rounded-md object-cover bg-gray-100 dark:bg-gray-700" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800 dark:text-gray-100">{t.title || '—'}</div>
                    <div className="text-xs text-gray-500 max-w-xs truncate">{t.description}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {t.linkText ? `${t.linkText} → ${t.linkUrl || '—'}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{t.sortOrder}</td>
                  <td className="px-4 py-3">
                    <InlineToggle
                      checked={t.active}
                      onToggle={(next) =>
                        update.mutateAsync({
                          id: t.id,
                          body: {
                            title: t.title,
                            description: t.description,
                            imageUrl: t.imageUrl,
                            linkUrl: t.linkUrl,
                            linkText: t.linkText,
                            sortOrder: t.sortOrder,
                            active: next,
                          },
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(t)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit tile">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          confirmDelete(t.title || 'this tile', async () => {
                            await del.mutateAsync(t.id)
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete tile"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <EditModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New Promo Tile"
        fields={fields}
        initialValues={{ sortOrder: tiles.length, active: true }}
        submitLabel="Create"
        onSubmit={async (values) => {
          try {
            await create.mutateAsync(toInput(values))
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />

      <EditModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Promo Tile"
        description={editing?.title}
        fields={fields}
        initialValues={
          editing
            ? {
                imageUrl: editing.imageUrl,
                title: editing.title,
                description: editing.description,
                linkText: editing.linkText,
                linkUrl: editing.linkUrl,
                sortOrder: editing.sortOrder,
                active: editing.active,
              }
            : {}
        }
        onSubmit={async (values) => {
          if (!editing) return
          try {
            await update.mutateAsync({ id: editing.id, body: toInput(values) })
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/promo-tiles/')({
  component: PromoTilesPage,
})
